// Weights modal (docs/05-raster-grading.md §8). Edits the project's grading
// weights as compact raster-style tables — styled like the Settings → Raster
// overview, but with a single editable weight column in place of the six grade
// columns.
//
// Two kinds of number live here, and they are presented differently:
//   • the language:content split is a partition (the pair sums to 1 — a share
//     of the overall grade), shown as its own full-width band with a proportion
//     bar and percentage inputs at the top;
//   • every other field is a relative multiplier (default 1, unbounded), shown
//     in the criterion/section tables below. A multiplier that differs from the
//     default is highlighted and carries a reset control.
//
// The tables flow into two top-aligned columns so the picture stays readable
// (the modal scrolls if it doesn't fit). These are the project-level raster
// defaults a task inherits; exam-specific per-task weights are NOT edited here —
// they live in the exam detail panel, since tasks may not even be detected yet
// at project setup.
//
// Criterion/section/subcriterion weights persist in rubric_config
// (language_parts + node_weights).

import { useState } from "react";
import type { ReactNode } from "react";
import { useSWRConfig } from "swr";
import { useTranslation } from "react-i18next";

import { apiJson } from "../../api/client";
import { useRasterCatalog } from "../../api/hooks";
import type { Project, RasterCriterion, RubricConfig, WeightPart } from "../../api/types";
import { toast } from "../../lib/toast";
import "../CreateProjectModal.css";
import "./WeightsPanel.css";

const PROJECTS_KEY = "/api/projects/?archived=0";
const LANG_KEYS = ["lang.lexik", "lang.grammatik", "lang.textgestaltung"];

// Clamp to [0, 1] and round to two decimals to avoid float noise in the split.
const splitVal = (v: number) => Math.round(Math.min(1, Math.max(0, v)) * 100) / 100;

interface Props {
  project: Project;
  onClose: () => void;
}

export function WeightsPanel({ project, onClose }: Props) {
  const { t } = useTranslation();
  const { mutate } = useSWRConfig();
  const { data: catalog } = useRasterCatalog();

  const rubric = project.rubric_config;
  const [languageWeight, setLanguageWeight] = useState(rubric?.language_weight ?? 0.4);
  const [contentWeight, setContentWeight] = useState(rubric?.content_weight ?? 0.6);
  const [langParts, setLangParts] = useState<WeightPart[]>(
    LANG_KEYS.map((k) => ({
      key: k,
      weight: rubric?.language_parts?.find((p) => p.key === k)?.weight ?? 1,
    })),
  );
  const [nodeWeights, setNodeWeights] = useState<Record<string, number>>({ ...(rubric?.node_weights ?? {}) });
  const [saving, setSaving] = useState(false);

  const nodeW = (key: string) => nodeWeights[key] ?? 1;
  const setNode = (key: string, v: number) => setNodeWeights((prev) => ({ ...prev, [key]: v }));
  function setLangPart(key: string, weight: number) {
    setLangParts((prev) => prev.map((p) => (p.key === key ? { ...p, weight } : p)));
  }

  // Language:content is a partition — the pair always sums to 1.
  function onLanguageWeight(v: number) {
    const lang = splitVal(v);
    setLanguageWeight(lang);
    setContentWeight(splitVal(1 - lang));
  }
  function onContentWeight(v: number) {
    const content = splitVal(v);
    setContentWeight(content);
    setLanguageWeight(splitVal(1 - content));
  }

  // Content default tables: the language criteria, one table per Schreiben
  // Teilaufgabe, and a combined Sprachmittlung (AFB IV) built from its three
  // criteria. They flow as a flat list of blocks distributed across two
  // top-aligned columns (alternating, to keep the columns roughly balanced).
  const schreibenTables = (catalog?.content_areas.schreiben ?? []).map((c) => ({ title: c.label, criteria: [c] }));
  const smTable = { title: "Sprachmittlung (AFB IV)", criteria: catalog?.content_areas.sprachmittlung ?? [] };

  const blocks: ReactNode[] = [
    <WtBlock key="__lang" title={t("weights.language_sub")}>
      {(catalog?.language_criteria ?? []).map((c) => (
        <LanguageRows
          key={c.key}
          criterion={c}
          critWeight={langParts.find((p) => p.key === c.key)?.weight ?? 1}
          onCritWeight={(v) => setLangPart(c.key, v)}
          nodeW={nodeW}
          setNode={setNode}
          disabled={saving}
        />
      ))}
    </WtBlock>,
    ...schreibenTables.map((tbl) => (
      <ContentBlock key={tbl.title} tbl={tbl} nodeW={nodeW} setNode={setNode} disabled={saving} />
    )),
    <ContentBlock key={smTable.title} tbl={smTable} nodeW={nodeW} setNode={setNode} disabled={saving} />,
  ];
  const colA = blocks.filter((_, i) => i % 2 === 0);
  const colB = blocks.filter((_, i) => i % 2 === 1);

  async function onSave() {
    setSaving(true);
    try {
      // Persist only non-default node weights to keep the config lean.
      const pruned: Record<string, number> = {};
      for (const [k, v] of Object.entries(nodeWeights)) if (v !== 1) pruned[k] = v;
      const rubricBody: RubricConfig = {
        language_weight: languageWeight,
        content_weight: contentWeight,
        language_parts: langParts,
        node_weights: pruned,
      };
      await apiJson(`/api/projects/${project.id}`, {
        method: "PATCH",
        body: JSON.stringify({ rubric_config: rubricBody }),
      });
      await mutate(PROJECTS_KEY);
      toast(t("weights.saved"));
      onClose();
    } catch {
      toast(t("weights.save_failed"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !saving) onClose();
      }}
    >
      <div className="modal-card weights-modal" role="dialog" aria-modal="true">
        <button type="button" className="modal-close" onClick={onClose} disabled={saving} aria-label="Close">✕</button>
        <h2 className="modal-card__title">{t("weights.title")}</h2>

        <div className="weights-body">
          <SplitBand
            languageWeight={languageWeight}
            onLanguageWeight={onLanguageWeight}
            onContentWeight={onContentWeight}
            disabled={saving}
          />

          <div className="weights-grid">
            <div className="weights-col">{colA}</div>
            <div className="weights-col">{colB}</div>
          </div>
        </div>

        <div className="modal-actions">
          <button type="button" className="modal-btn modal-btn--ghost" onClick={onClose} disabled={saving}>
            {t("common.cancel")}
          </button>
          <button type="button" className="modal-btn modal-btn--accent" onClick={() => void onSave()} disabled={saving}>
            {saving ? t("common.saving") : t("common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}

// The language:content split — a partition of the overall grade. A proportion
// bar makes the share visible at a glance; the two percentage inputs edit it
// (each adjusts the other so the pair always sums to 100%).
function SplitBand({
  languageWeight,
  onLanguageWeight,
  onContentWeight,
  disabled,
}: {
  languageWeight: number;
  onLanguageWeight: (v: number) => void;
  onContentWeight: (v: number) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  const langPct = Math.round(languageWeight * 100);
  const contentPct = 100 - langPct;
  return (
    <div className="split-band">
      <div className="split-band__head">
        <span className="split-band__title">{t("weights.split")}</span>
        <span className="split-band__hint">{t("weights.split_hint")}</span>
      </div>
      <div className="split-band__bar" role="img" aria-label={`${langPct}% / ${contentPct}%`}>
        <div className="split-band__seg split-band__seg--lang" style={{ width: `${langPct}%` }}>
          {langPct >= 12 && `${langPct}%`}
        </div>
        <div className="split-band__seg split-band__seg--content" style={{ width: `${contentPct}%` }}>
          {contentPct >= 12 && `${contentPct}%`}
        </div>
      </div>
      <div className="split-band__fields">
        <SplitField label={t("raster.group_language")} pct={langPct} onPct={(p) => onLanguageWeight(p / 100)} disabled={disabled} swatch="lang" />
        <SplitField label={t("raster.group_content")} pct={contentPct} onPct={(p) => onContentWeight(p / 100)} disabled={disabled} swatch="content" />
      </div>
    </div>
  );
}

function SplitField({
  label,
  pct,
  onPct,
  disabled,
  swatch,
}: {
  label: string;
  pct: number;
  onPct: (p: number) => void;
  disabled: boolean;
  swatch: "lang" | "content";
}) {
  return (
    <label className="split-field">
      <span className={`split-field__swatch split-field__swatch--${swatch}`} aria-hidden="true" />
      <span className="split-field__label">{label}</span>
      <span className="split-field__input">
        <input
          type="number"
          min={0}
          max={100}
          step={5}
          value={pct}
          onChange={(e) => onPct(Number(e.target.value))}
          disabled={disabled}
        />
        <span className="split-field__pct">%</span>
      </span>
    </label>
  );
}

// One content default table (a Schreiben Teilaufgabe, or the combined
// Sprachmittlung) rendered as a titled weight table.
function ContentBlock({
  tbl,
  nodeW,
  setNode,
  disabled,
}: {
  tbl: { title: string; criteria: RasterCriterion[] };
  nodeW: (key: string) => number;
  setNode: (key: string, v: number) => void;
  disabled: boolean;
}) {
  return (
    <WtBlock title={tbl.title}>
      {tbl.criteria.flatMap((c) => c.groups).map((g) => (
        <GroupRows key={g.key} group={g} nodeW={nodeW} setNode={setNode} disabled={disabled} />
      ))}
    </WtBlock>
  );
}

// A titled raster-style table: a banner heading over a two-column table whose
// rows (passed as children) are label + editable weight.
function WtBlock({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="wt-block">
      <div className="wt-table-wrap">
        <div className="wt-title" title={title}>{title}</div>
        <table className="wt-table">
          <tbody>{children}</tbody>
        </table>
      </div>
    </div>
  );
}

// One language criterion: its label + weight (head level), then its subcriteria
// indented as leaf rows (the single group's heading repeats the label, so the
// group heading itself is skipped — only its rows are shown).
function LanguageRows({
  criterion,
  critWeight,
  onCritWeight,
  nodeW,
  setNode,
  disabled,
}: {
  criterion: RasterCriterion;
  critWeight: number;
  onCritWeight: (v: number) => void;
  nodeW: (key: string) => number;
  setNode: (key: string, v: number) => void;
  disabled: boolean;
}) {
  const rows = criterion.groups.flatMap((g) => g.rows);
  return (
    <>
      <WtRow level="head" label={criterion.label} value={critWeight} onChange={onCritWeight} disabled={disabled} />
      {rows.map((r) => (
        <WtRow key={r.key} level="leaf" label={r.label} value={nodeW(r.key)} onChange={(v) => setNode(r.key, v)} disabled={disabled} />
      ))}
    </>
  );
}

// One content section: a heading row (with a section weight) + indented leaf
// rows, or — for a headingless section — its rows shown at head level.
function GroupRows({
  group,
  nodeW,
  setNode,
  disabled,
}: {
  group: RasterCriterion["groups"][number];
  nodeW: (key: string) => number;
  setNode: (key: string, v: number) => void;
  disabled: boolean;
}) {
  if (!group.heading) {
    return (
      <>
        {group.rows.map((r) => (
          <WtRow key={r.key} level="head" label={r.label} value={nodeW(r.key)} onChange={(v) => setNode(r.key, v)} disabled={disabled} />
        ))}
      </>
    );
  }
  return (
    <>
      <WtRow level="head" label={group.heading} value={nodeW(group.key)} onChange={(v) => setNode(group.key, v)} disabled={disabled} />
      {group.rows.map((r) => (
        <WtRow key={r.key} level="leaf" label={r.label} value={nodeW(r.key)} onChange={(v) => setNode(r.key, v)} disabled={disabled} />
      ))}
    </>
  );
}

function WtRow({
  label,
  level,
  value,
  onChange,
  disabled,
}: {
  label: string;
  level: "head" | "leaf";
  value: number;
  onChange: (v: number) => void;
  disabled: boolean;
}) {
  return (
    <tr className={`wt-row wt-row--${level}`}>
      <td className="wt-label">{label}</td>
      <td className="wt-w">
        <WeightField value={value} onChange={onChange} disabled={disabled} />
      </td>
    </tr>
  );
}

// A relative multiplier field (default 1). When the value differs from the
// default it is highlighted and offers a reset control, so a deliberately
// customised weight stands out from the wall of defaults.
function WeightField({
  value,
  onChange,
  disabled,
}: {
  value: number;
  onChange: (v: number) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  const changed = value !== 1;
  return (
    <span className={`wt-field-wrap${changed ? " wt-field-wrap--changed" : ""}`}>
      <input
        type="number"
        className="wt-field"
        min={0}
        step="0.1"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        disabled={disabled}
      />
      {changed && !disabled && (
        <button
          type="button"
          className="wt-reset"
          title={t("weights.reset")}
          aria-label={t("weights.reset")}
          onClick={() => onChange(1)}
        >
          ↺
        </button>
      )}
    </span>
  );
}
